import {
  Box,
  Button,
  FormControl,
  FormControlLabel,
  FormLabel,
  Link,
  Radio,
  RadioGroup,
  TextField,
} from '@mui/material';
import React, { useCallback, useMemo, useState } from 'react';
import { HashLoader } from 'react-spinners';
import type { MfaChallenge, MfaChannel } from '../../types/authMfa';

const CODE_PATTERN = /^[0-9]{4,10}$/;

const channelLabel = (c: MfaChannel): string => {
  switch (c) {
    case 'sms':
      return 'Text message (SMS)';
    case 'email':
      return 'Email';
    case 'totp':
      return 'Authenticator app';
    default:
      return c;
  }
};

export interface MfaVerifyStepProps {
  challenge: MfaChallenge;
  loading: boolean;
  /** True while starting SMS/email send after the user picks a method. */
  dispatchLoading?: boolean;
  onSubmit: (channel: MfaChannel, code: string) => void;
  /** Required when the user may need to choose SMS or email before a code is sent (`channels.length > 1`). */
  onDispatch?: (channel: 'sms' | 'email') => Promise<void>;
  onBack: () => void;
  /** Default: "Back to sign in" — use e.g. "Cancel" for step-up modal. */
  backButtonLabel?: string;
}

const MfaVerifyStep: React.FC<MfaVerifyStepProps> = ({
  challenge,
  loading,
  dispatchLoading = false,
  onSubmit,
  onDispatch,
  onBack,
  backButtonLabel = 'Back to sign in',
}) => {
  const channels = challenge.channels ?? [];
  const isMulti = channels.length > 1;

  const [phase, setPhase] = useState<'pick' | 'code'>(() => (isMulti ? 'pick' : 'code'));
  const [selectedChannel, setSelectedChannel] = useState<MfaChannel | null>(() =>
    !isMulti && channels[0] ? channels[0] : null
  );
  const [code, setCode] = useState('');

  const needsOutboundDispatch = useCallback(
    (c: MfaChannel) =>
      (c === 'sms' && !challenge.smsDispatched) || (c === 'email' && !challenge.emailDispatched),
    [challenge.emailDispatched, challenge.smsDispatched]
  );

  const hintLines = useMemo(() => {
    if (phase === 'pick' && isMulti) {
      return [
        'Choose how you want to verify. For SMS or email we will send a code after you continue; for an authenticator app, use the code from the app.',
      ];
    }
    if (phase === 'code' && selectedChannel === 'totp') {
      return ['Open your authenticator app and enter the current code.'];
    }
    const lines: string[] = [];
    if (challenge.smsDispatched && challenge.phoneMasked) {
      lines.push(`A code was sent via SMS to ${challenge.phoneMasked}.`);
    } else if (channels.includes('sms') && challenge.phoneMasked && phase === 'code') {
      lines.push(`SMS: ${challenge.phoneMasked}`);
    }
    if (challenge.emailDispatched && challenge.emailMasked) {
      lines.push(`A code was sent via email to ${challenge.emailMasked}.`);
    } else if (channels.includes('email') && challenge.emailMasked && phase === 'code') {
      lines.push(`Email: ${challenge.emailMasked}`);
    }
    return lines;
  }, [challenge, channels, phase, isMulti, selectedChannel]);

  const otpChannelReady =
    selectedChannel === 'totp' ||
    (selectedChannel === 'sms' && challenge.smsDispatched) ||
    (selectedChannel === 'email' && challenge.emailDispatched);

  const codeValid = CODE_PATTERN.test(code.trim());
  const busy = loading || dispatchLoading;
  const canSubmit =
    phase === 'code' && codeValid && !busy && selectedChannel != null && channels.includes(selectedChannel) && otpChannelReady;

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      if (!canSubmit || !selectedChannel) return;
      onSubmit(selectedChannel, code.trim());
    },
    [canSubmit, code, onSubmit, selectedChannel]
  );

  const handleCodeChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value.replace(/\D/g, '').slice(0, 10);
    setCode(v);
  }, []);

  const handleContinueFromPick = useCallback(async () => {
    if (!selectedChannel || busy) return;
    if (selectedChannel === 'totp') {
      setPhase('code');
      setCode('');
      return;
    }
    if (selectedChannel === 'sms' || selectedChannel === 'email') {
      try {
        if (needsOutboundDispatch(selectedChannel) && onDispatch) {
          await onDispatch(selectedChannel);
        }
        setPhase('code');
        setCode('');
      } catch {
        /* Parent shows alert */
      }
    }
  }, [busy, needsOutboundDispatch, onDispatch, selectedChannel]);

  const handleChangeMethod = useCallback(() => {
    setPhase('pick');
    setSelectedChannel(null);
    setCode('');
  }, []);

  const continueDisabled = busy || !selectedChannel;

  return (
    <Box sx={{ mt: 1 }}>
      {phase === 'pick' && isMulti && (
        <Box sx={{ mb: 2 }}>
          {hintLines.map(line => (
            <p key={line} style={{ fontSize: 14, margin: '8px 0', color: 'rgba(0,0,0,0.7)' }}>
              {line}
            </p>
          ))}
          <FormControl component="fieldset" className="login-fields" margin="normal" fullWidth>
            <FormLabel component="legend">Verification method</FormLabel>
            <RadioGroup
              value={selectedChannel ?? ''}
              onChange={(_, v) => setSelectedChannel(v as MfaChannel)}
              name="mfa-channel-pick"
            >
              {channels.map(c => (
                <FormControlLabel key={c} value={c} control={<Radio size="small" />} label={channelLabel(c)} />
              ))}
            </RadioGroup>
          </FormControl>
          <Button
            size="medium"
            type="button"
            variant="contained"
            className="full-width"
            disabled={continueDisabled}
            onClick={() => void handleContinueFromPick()}
          >
            {dispatchLoading ? <HashLoader size={30} color="#fff" speedMultiplier={0.8} /> : 'Continue'}
          </Button>
        </Box>
      )}

      {phase === 'code' && (
        <form onSubmit={handleSubmit}>
          <div className="login-fields-container" style={{ marginTop: 8 }}>
            {hintLines.map(line => (
              <p key={line} style={{ fontSize: 14, margin: '8px 0', color: 'rgba(0,0,0,0.7)' }}>
                {line}
              </p>
            ))}
            {isMulti && (
              <Box sx={{ mb: 1 }}>
                <Link
                  component="button"
                  type="button"
                  variant="body2"
                  onClick={handleChangeMethod}
                  disabled={busy}
                  sx={{ cursor: 'pointer' }}
                >
                  Use a different verification method
                </Link>
              </Box>
            )}
            <TextField
              id="mfa-code-input"
              name="mfaCode"
              size="small"
              label="Verification code"
              value={code}
              onChange={handleCodeChange}
              className="login-fields"
              fullWidth
              autoComplete="one-time-code"
              inputProps={{ inputMode: 'numeric', pattern: '[0-9]*', maxLength: 10, 'aria-label': 'Verification code' }}
              helperText="Enter the 4–10 digit code"
            />
          </div>
          <Button size="medium" type="submit" variant="contained" className="full-width" disabled={!canSubmit}>
            {loading ? <HashLoader size={30} color="#1b6bae" speedMultiplier={0.8} /> : 'Verify'}
          </Button>
        </form>
      )}

      <Button
        size="small"
        type="button"
        variant="text"
        className="full-width mt-2"
        onClick={onBack}
        disabled={busy}
      >
        {backButtonLabel}
      </Button>
    </Box>
  );
};

export default MfaVerifyStep;
